import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Modal,
  Input,
  Select,
  TimePicker,
  Popconfirm,
  Spin,
  Tooltip,
} from "antd";
import { UsergroupAddOutlined, InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";

import studyprojectgroupService from "../../../../services/studentaistudyprojectgroups/studentaistudyprojectgroupsService";
import aistudyprojectService from "../../../../services/aistudyproject/aistudyprojectService";
import spNotify from "../spNotify";

import "../spTheme.css";
import "../index.css";
import "./index.css";

const MARKS = ["📚", "🧪", "🧮", "🌍", "🎨", "⚡", "🦉", "🚀"];
// What the deadline is stored and sent as: 24-hour wall-clock, because the
// session job reads it apart on the colon (model/studentaistudyprojectgroups).
// Students never see it in that shape — DEADLINE_DISPLAY is the one they read
// and pick, on a clock with AM and PM.
const DEADLINE_FORMAT = "HH:mm";
const DEADLINE_DISPLAY = "h:mm A";

// What the upload deadline means, spelled out for the chip's tooltip — the
// chip alone reads like a start time, not a cutoff.
const DROP_DEADLINE_HINT =
  "This is a cutoff, not a start time. Add your notes for today's class before it — once it passes, everyone in the group gets one shared summary built from what came in.";

// The zone this student's browser is in. A deadline is a wall-clock time, so
// it only means a moment once it is paired with the zone it was set in — the
// group carries that zone, and the session job builds the real instant from
// the two of them.
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (ex) {
    return "UTC";
  }
};

// What a zone is called in a chip: "PST", "GMT+5:30".
const zoneLabel = (timezone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((part) => part.type === "timeZoneName")?.value || "";
  } catch (ex) {
    return "";
  }
};

// "21:00" reads as "9:00 PM" — and when the group keeps a different clock than
// this student's browser, whose clock that is, so nobody debriefs an hour late.
export const deadlineLabel = (deadlinetime, timezone) => {
  const time = moment(deadlinetime || "21:00", DEADLINE_FORMAT).format(
    DEADLINE_DISPLAY
  );
  if (!timezone || timezone === browserTimeZone()) return time;
  const zone = zoneLabel(timezone);
  return zone ? `${time} ${zone}` : time;
};

// A zone's own calendar and clock at some instant. Intl is the only zone table
// in the app — there is no moment-timezone here — so the parts come from a
// formatter and are read back as numbers.
const zoneParts = (instant, timezone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const out = {};
  for (const part of parts)
    if (part.type !== "literal") out[part.type] = Number(part.value);
  return out;
};

// How far the zone is from UTC at that instant, DST included.
const zoneOffset = (instant, timezone) => {
  const p = zoneParts(instant, timezone);
  // Some engines write midnight as hour 24 under hour12: false.
  return (
    Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) -
    instant.getTime()
  );
};

// The instant a wall-clock time on a zone's own date actually happens.
const instantFor = (deadlinetime, timezone) => {
  const [hh, mm] = (deadlinetime || "21:00").split(":").map(Number);
  const today = zoneParts(new Date(), timezone);
  const guess = Date.UTC(today.year, today.month - 1, today.day, hh, mm);
  // Two passes: the offset at the guess can belong to the other side of a DST
  // change than the offset at the answer.
  const first = new Date(guess - zoneOffset(new Date(guess), timezone));
  return new Date(guess - zoneOffset(first, timezone));
};

// The same moment on the reader's own clock: "10:30 AM your time", plus the
// day it lands on when their date is not the group's. Empty when the group
// already keeps this student's clock — then there is nothing to convert.
export const localDeadlineLabel = (deadlinetime, timezone) => {
  if (!timezone || timezone === browserTimeZone()) return "";
  try {
    const instant = instantFor(deadlinetime, timezone);
    const there = zoneParts(instant, timezone);
    const time = moment(instant).format(DEADLINE_DISPLAY);
    // Whole calendar days apart, so a month or year end cannot flip the sign.
    const shift = moment(instant)
      .startOf("day")
      .diff(
        moment(`${there.year}-${there.month}-${there.day}`, "YYYY-M-D"),
        "days"
      );
    if (shift === 0) return `${time} your time`;
    return shift > 0
      ? `${time} your time, next day`
      : `${time} your time, the day before`;
  } catch (ex) {
    return "";
  }
};
/**
 * Study Project · Study Groups. A group is a few students taking the same
 * class: each does their own short debrief and everyone gets the one course
 * Blast builds from all of them together.
 *
 * A group with one member is waiting, not working — the student keeps
 * debriefing exactly as they do today until someone else joins.
 *
 * This screen never shows who has not debriefed, and keeps no record of who
 * took part on past days. Neither may anything built on top of it.
 */
class StudyProjectGroups extends Component {
  state = {
    groups: [],
    courses: [],
    // Invites opened but not answered — usually because the class was not in
    // this student's list yet. The class they pick for each one, by code.
    pending: [],
    pendingCourse: {},
    loading: true,
    // The "start a group" form.
    creating: false,
    newCourseId: null,
    newName: "",
    newMark: MARKS[0],
    newDeadline: moment("21:00", DEADLINE_FORMAT),
    saving: false,
    // The group whose settings the manager is editing.
    editing: null,
    editName: "",
    editMark: MARKS[0],
    editDeadline: moment("21:00", DEADLINE_FORMAT),
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true });
    const [courses] = await Promise.all([
      aistudyprojectService.fetchStudyCourses(),
      this.refreshGroups(),
    ]);
    this.setState({
      courses: Array.isArray(courses) ? courses : [],
      loading: false,
    });
  };

  // Groups and pending invites, the two lists every action on this page can
  // change. The class list (this.state.courses) never does, so it is only
  // fetched once, on mount. The Drop itself lives on the Group Material page.
  refreshGroups = async () => {
    const [groups, pending] = await Promise.all([
      studyprojectgroupService.fetchMyGroups(),
      studyprojectgroupService.fetchPendingInvites(),
    ]);
    this.setState({
      groups: Array.isArray(groups) ? groups : [],
      pending: Array.isArray(pending) ? pending : [],
    });
  };

  // Joining from the card is the same join the invite link does — the class is
  // the one thing only this student can answer.
  joinPending = async (invite) => {
    const courseid = this.state.pendingCourse[invite.invitecode];
    if (!courseid) {
      spNotify.warning("Pick the class this group is for.");
      return;
    }

    this.setState({ saving: true });
    const result = await studyprojectgroupService.joinGroup({
      invitecode: invite.invitecode,
      courseid,
    });
    this.setState({ saving: false });

    if (result?.apierror) {
      spNotify.error(result.apierror);
      return;
    }
    spNotify.success(
      "You're in. Everything the group has already built is yours."
    );
    this.refreshGroups();
  };

  // The group is not told, and the link still works if they change their mind.
  // Nothing else on the page depends on this invite, so there's nothing to
  // refetch — it just comes off the list.
  dismissPending = async (invite) => {
    const result = await studyprojectgroupService.dismissInvite(
      invite.invitecode
    );
    if (result?.apierror) {
      spNotify.error(result.apierror);
      return;
    }
    this.setState((state) => ({
      pending: state.pending.filter(
        (item) => item.invitecode !== invite.invitecode
      ),
    }));
  };

  // A class can only be in one group, so a debrief is never split in two.
  freeCourses = () =>
    this.state.courses.filter(
      (course) =>
        !this.state.groups.some(
          (group) => String(group.courseid) === String(course._id)
        )
    );

  inviteLink = (invitecode) =>
    `${window.location.origin}/studyproject/groups/join/${invitecode}`;

  copyInvite = async (group) => {
    const link = this.inviteLink(group.invitecode);
    try {
      await navigator.clipboard.writeText(link);
      spNotify.success("Invite link copied — send it to your classmates.");
    } catch (ex) {
      // Clipboard access can be refused; the code is on screen either way.
      spNotify.info(`Share this link: ${link}`);
    }
  };

  createGroup = async () => {
    const { newCourseId, newName, newMark, newDeadline } = this.state;
    if (!newCourseId || !newName.trim()) {
      spNotify.warning("Pick a class and give the group a name.");
      return;
    }

    this.setState({ saving: true });
    const result = await studyprojectgroupService.createGroup({
      name: newName.trim(),
      mark: newMark,
      courseid: newCourseId,
      deadlinetime: newDeadline.format(DEADLINE_FORMAT),
      timezone: browserTimeZone(),
    });
    this.setState({ saving: false });

    if (result?.apierror) {
      spNotify.error(result.apierror);
      return;
    }

    spNotify.success(
      "Your group is ready — invite a classmate to switch it on."
    );
    this.setState({ creating: false, newCourseId: null, newName: "" });
    this.refreshGroups();
  };

  openSettings = (group) =>
    this.setState({
      editing: group,
      editName: group.name,
      editMark: group.mark || MARKS[0],
      editDeadline: moment(group.deadlinetime || "21:00", DEADLINE_FORMAT),
    });

  saveSettings = async () => {
    const { editing, editName, editMark, editDeadline } = this.state;
    if (!editName.trim()) {
      spNotify.warning("The group needs a name.");
      return;
    }

    this.setState({ saving: true });
    const result = await studyprojectgroupService.updateGroup(editing._id, {
      name: editName.trim(),
      mark: editMark,
      deadlinetime: editDeadline.format(DEADLINE_FORMAT),
      // Re-stamped, not assumed: a manager who has moved sets the deadline on
      // the clock they are reading now.
      timezone: browserTimeZone(),
    });
    this.setState({ saving: false });

    if (result?.apierror) {
      spNotify.error(result.apierror);
      return;
    }
    spNotify.success("Saved.");
    this.setState({ editing: null });
    this.refreshGroups();
  };

  handOver = async (group, studentid) => {
    const result = await studyprojectgroupService.handOverManager(
      group._id,
      studentid
    );
    if (result?.apierror) {
      spNotify.error(result.apierror);
      return;
    }
    spNotify.success("Handed on. It was a chore, not a crown.");
    this.refreshGroups();
  };

  removeMember = async (group, studentid) => {
    const result = await studyprojectgroupService.removeMember(
      group._id,
      studentid
    );
    if (result?.apierror) {
      spNotify.error(result.apierror);
      return;
    }
    this.refreshGroups();
  };

  leave = async (group) => {
    const result = await studyprojectgroupService.leaveGroup(group._id);
    if (result?.apierror) {
      spNotify.error(result.apierror);
      return;
    }
    spNotify.success(
      "You have left the group. Everything built for you stays yours."
    );
    this.refreshGroups();
  };

  renderMember = (group, member, isManager) => (
    <div className="sg-member" key={member._id}>
      <span className="sg-member__name">
        {member.firstname} {member.lastname}
      </span>
      {!!member.email && (
        <span className="sg-member__email">{member.email}</span>
      )}
      {member.role === "manager" && (
        <span className="sp-chip sg-chip--manager">manager</span>
      )}
      {isManager && member.role !== "manager" && (
        <span className="sg-member__actions">
          <Popconfirm
            title="Hand the manager job to them?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => this.handOver(group, member.studentid)}
          >
            <button type="button" className="sp-btn sp-btn--link">
              make manager
            </button>
          </Popconfirm>
          <Popconfirm
            title="Remove them from the group?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => this.removeMember(group, member.studentid)}
          >
            <button type="button" className="sp-btn sp-btn--link">
              remove
            </button>
          </Popconfirm>
        </span>
      )}
    </div>
  );

  // An invite waiting on this student. It carries what the invite page shows —
  // the group, the class it was started for, when the day closes — plus the one
  // question only they can answer, so the invite can be taken up from here.
  renderPending = (invite) => {
    const free = this.freeCourses();
    const local = localDeadlineLabel(invite.deadlinetime, invite.timezone);

    return (
      <div className="sp-class-card sg-card" key={invite.invitecode}>
        <div className="sp-class-card__main">
          <div className="sp-class-card__name">
            <span className="sg-card__mark">{invite.mark}</span> {invite.name}
          </div>
          <div className="sp-chips">
            <span className="sp-chip sp-chip--tests">invitation waiting</span>
            {!!invite.coursename && (
              <span className="sp-chip">{invite.coursename}</span>
            )}
            <Tooltip title={DROP_DEADLINE_HINT}>
              <span className="sp-chip">
                Group Material Upload Deadline{" "}
                {deadlineLabel(invite.deadlinetime, invite.timezone)}{" "}
                <InfoCircleOutlined />
              </span>
            </Tooltip>
            {!!local && <span className="sp-chip">{local}</span>}
          </div>

          {free.length === 0 ? (
            <>
              <p className="sg-card__note">
                You were invited to this group for{" "}
                {invite.coursename || "a class"}. Add that class to your own
                list and this card is ready to join — the invite waits here
                until you do.
              </p>
              <button
                type="button"
                className="sp-btn sp-btn--outline"
                onClick={() =>
                  this.props.history.push("/studyproject/course/new")
                }
              >
                Add the class
              </button>
            </>
          ) : (
            <>
              <label className="sg-form__label">
                Which of your classes is this?
              </label>
              <Select
                className="sg-form__field"
                value={this.state.pendingCourse[invite.invitecode] || undefined}
                onChange={(courseid) =>
                  this.setState((state) => ({
                    pendingCourse: {
                      ...state.pendingCourse,
                      [invite.invitecode]: courseid,
                    },
                  }))
                }
                placeholder="Pick the matching class"
              >
                {free.map((course) => (
                  <Select.Option value={course._id} key={course._id}>
                    {course.coursename}
                  </Select.Option>
                ))}
              </Select>
              <button
                type="button"
                className="sp-add-btn"
                disabled={this.state.saving}
                onClick={() => this.joinPending(invite)}
              >
                <UsergroupAddOutlined />
                Join this group
              </button>
            </>
          )}

          <div className="sg-invite">
            <span className="sg-invite__code">{invite.invitecode}</span>
            <button
              type="button"
              className="sp-btn sp-btn--link"
              onClick={() =>
                this.props.history.push(
                  `/studyproject/groups/join/${invite.invitecode}`
                )
              }
            >
              open the invite
            </button>
          </div>
        </div>

        <div className="sp-class-card__actions">
          <Popconfirm
            title="Remove this invitation?"
            okText="Yes"
            cancelText="Keep it"
            onConfirm={() => this.dismissPending(invite)}
          >
            <button type="button" className="sp-btn sp-btn--link">
              Dismiss
            </button>
          </Popconfirm>
        </div>
      </div>
    );
  };

  renderGroup = (group) => {
    const isManager = group.role === "manager";
    const waiting = group.state === "pending";

    return (
      <div className="sp-class-card sg-card" key={group._id}>
        <div className="sp-class-card__main">
          <div className="sp-class-card__name">
            <span className="sg-card__mark">{group.mark}</span> {group.name}
          </div>
          <div className="sp-chips">
            <span className="sp-chip">{group.coursename}</span>
            <Tooltip title={DROP_DEADLINE_HINT}>
              <span className="sp-chip">
                Group Material Upload Deadline{" "}
                {deadlineLabel(group.deadlinetime, group.timezone)}{" "}
                <InfoCircleOutlined />
              </span>
            </Tooltip>
            {!!localDeadlineLabel(group.deadlinetime, group.timezone) && (
              <span className="sp-chip">
                {localDeadlineLabel(group.deadlinetime, group.timezone)}
              </span>
            )}
            <span className={`sp-chip ${waiting ? "" : "sp-chip--tests"}`}>
              {waiting
                ? "waiting for one more"
                : `${group.members.length} members`}
            </span>
          </div>

          {waiting && (
            <p className="sg-card__note">
              Until someone else joins you are studying the way you always have.
              Send them the invite link and the group switches on by itself.
            </p>
          )}

          {!waiting && (
            <Link className="sp-btn sp-btn--link" to="/studyproject/groups/upload">
              Group material →
            </Link>
          )}

          <div className="sg-members">
            {group.members.map((member) =>
              this.renderMember(group, member, isManager)
            )}
          </div>

          <div className="sg-invite">
            <span className="sg-invite__code">{group.invitecode}</span>
            <button
              type="button"
              className="sp-btn sp-btn--link"
              onClick={() => this.copyInvite(group)}
            >
              copy invite link
            </button>
          </div>
        </div>

        <div className="sp-class-card__actions">
          {isManager && (
            <button
              type="button"
              className="sp-btn sp-btn--outline"
              onClick={() => this.openSettings(group)}
            >
              Group settings
            </button>
          )}
          <Popconfirm
            title="Leave this group?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => this.leave(group)}
          >
            <button
              type="button"
              className="sp-btn sp-btn--outline sp-btn--blue"
            >
              Leave
            </button>
          </Popconfirm>
        </div>
      </div>
    );
  };

  render() {
    const {
      groups,
      pending,
      loading,
      creating,
      newCourseId,
      newName,
      newMark,
      newDeadline,
      saving,
      editing,
      editName,
      editMark,
      editDeadline,
    } = this.state;
    const free = this.freeCourses();

    return (
      <div className="blast-sp sp-page sp-classes">
        <div className="blast-sp__head">
          <div className="blast-sp__eyebrow">Study Groups · Step 1 of 3</div>
          <h1 className="blast-sp__title">My Groups</h1>
          <p className="blast-sp__intro">
            Four or five of you in the same class, each doing your own short
            debrief. Blast puts them together and builds one course from the
            fuller picture — and everyone gets it, including anyone who had a
            busy day.
          </p>
        </div>

        {loading ? (
          <div className="sp-classes__loading">
            <Spin />
          </div>
        ) : (
          <div className="sp-classes__list">
            {pending.map(this.renderPending)}
            {groups.length === 0 && pending.length === 0 && (
              <div className="sp-classes__empty sg-empty-note">
                No study groups yet — start one for a class below.
                <span className="sg-empty-note__line2">
                  To add a class click{" "}
                  <Link to="/studyproject/course/new">"Add A Class"</Link>
                </span>
              </div>
            )}
            {groups.map(this.renderGroup)}
          </div>
        )}

        <button
          type="button"
          className="sp-add-btn"
          onClick={() => this.setState({ creating: true })}
        >
          + Start a study group
        </button>

        <Modal
          visible={creating}
          centered
          title="Start a study group"
          okText="Create group"
          cancelText={free.length === 0 ? "Close" : "Cancel"}
          onOk={this.createGroup}
          onCancel={() => this.setState({ creating: false })}
          okButtonProps={{ loading: saving, style: free.length === 0 ? { display: "none" } : undefined }}
        >
          {free.length === 0 ? (
            <p>
              Every class in your list is already in a group. Add another
              class first, then come back. To add a class click{" "}
              <Link to="/studyproject/course/new">"Add A Class"</Link>
            </p>
          ) : (
            <div className="sg-form">
              <label className="sg-form__label">Which class?</label>
              <Select
                className="sg-form__field"
                value={newCourseId}
                onChange={(newCourseId) => this.setState({ newCourseId })}
                placeholder="Pick one of your classes"
              >
                {free.map((course) => (
                  <Select.Option value={course._id} key={course._id}>
                    {course.coursename}
                  </Select.Option>
                ))}
              </Select>

              <label className="sg-form__label">Name your group</label>
              <Input
                className="sg-form__field"
                value={newName}
                maxLength={40}
                onChange={(e) => this.setState({ newName: e.target.value })}
              />

              <label className="sg-form__label">Pack an appropriate mark</label>
              <Select
                className="sg-form__field"
                value={newMark}
                onChange={(newMark) => this.setState({ newMark })}
              >
                {MARKS.map((mark) => (
                  <Select.Option value={mark} key={mark}>
                    {mark}
                  </Select.Option>
                ))}
              </Select>

              <label className="sg-form__label">
                What is the deadline for uploading content after each class?
              </label>
              <TimePicker
                className="sg-form__field"
                value={newDeadline}
                use12Hours
                format={DEADLINE_DISPLAY}
                minuteStep={15}
                allowClear={false}
                onChange={(newDeadline) =>
                  newDeadline && this.setState({ newDeadline })
                }
              />
              <p className="sg-form__hint">
                Study material uploaded prior to the deadline will be used to
                create the group study guide. The time is your own —{" "}
                {zoneLabel(browserTimeZone()) || browserTimeZone()}.
              </p>
            </div>
          )}
        </Modal>

        <Modal
          visible={!!editing}
          centered
          title="Group settings"
          okText="Save"
          onOk={this.saveSettings}
          onCancel={() => this.setState({ editing: null })}
          okButtonProps={{ loading: saving }}
        >
          <div className="sg-form">
            <label className="sg-form__label">Group name</label>
            <Input
              className="sg-form__field"
              value={editName}
              maxLength={40}
              onChange={(e) => this.setState({ editName: e.target.value })}
            />

            <label className="sg-form__label">Mark</label>
            <Select
              className="sg-form__field"
              value={editMark}
              onChange={(editMark) => this.setState({ editMark })}
            >
              {MARKS.map((mark) => (
                <Select.Option value={mark} key={mark}>
                  {mark}
                </Select.Option>
              ))}
            </Select>

            <label className="sg-form__label">Deadline time</label>
            <TimePicker
              className="sg-form__field"
              value={editDeadline}
              use12Hours
              format={DEADLINE_DISPLAY}
              minuteStep={15}
              allowClear={false}
              onChange={(editDeadline) =>
                editDeadline && this.setState({ editDeadline })
              }
            />
            <p className="sg-form__hint">
              The time is your own —{" "}
              {zoneLabel(browserTimeZone()) || browserTimeZone()}.
              {editing && editing.timezone !== browserTimeZone()
                ? ` This group's day currently closes at ${deadlineLabel(
                    editing.deadlinetime,
                    editing.timezone
                  )}; saving puts it on your clock.`
                : ""}
            </p>
          </div>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = () => ({});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(StudyProjectGroups);
